import React from "react";
import { isEmpty } from "lodash";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Table from "examples/Tables/Table";
import Author from "layouts/dashboard/components/TableAuthor";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import { ADMIN_USERS_COLUMNS, USER_MANAGEMENT_MESSAGES } from "../userManagement.constants";

// Shown on the disabled buttons so it is clear why they can't be used.
const PROTECTED_TOOLTIP = "Super Admin accounts are protected";

function AdminUsersTable({
  adminUsers,
  currentAdminId,
  onEdit,
  onToggleActive,
  onDelete,
  isLoading,
}) {
  const rows = adminUsers.map((user) => {
    const isSelf = user.id === currentAdminId;
    // Other Super Admins are read-only. You can still edit your own account
    // (e.g. change your password) but not deactivate or delete yourself.
    const isLocked = user.is_super_admin && !isSelf;
    const lockedTooltip = isLocked
      ? PROTECTED_TOOLTIP
      : isSelf
      ? "You can't change your own access"
      : null;
    return {
      user: <Author name={`${user.name}${isSelf ? " (you)" : ""}`} email={user.email} />,
      role: user.role ? <GoalPill color="info">{user.role.name}</GoalPill> : "—",
      teams: (
        <SuiTypography variant="caption" color="secondary">
          {user.teams.map((team) => team.name).join(", ") || "—"}
        </SuiTypography>
      ),
      status: (
        <GoalPill color={user.is_active ? "success" : "secondary"}>
          {user.is_active ? "Active" : "Inactive"}
        </GoalPill>
      ),
      actions: (
        <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
          <GoalActionButton
            icon="edit"
            tooltip={lockedTooltip && isLocked ? PROTECTED_TOOLTIP : "Edit user"}
            color="info"
            disabled={isLocked}
            onClick={() => onEdit(user)}
          />
          <GoalActionButton
            icon={user.is_active ? "block" : "check_circle"}
            tooltip={lockedTooltip || (user.is_active ? "Deactivate" : "Reactivate")}
            color={user.is_active ? "warning" : "success"}
            disabled={isLocked || isSelf}
            onClick={() => onToggleActive(user)}
          />
          <GoalActionButton
            icon="delete_outline"
            tooltip={lockedTooltip || "Delete user"}
            color="error"
            disabled={isLocked || isSelf}
            onClick={() => onDelete(user)}
          />
        </SuiBox>
      ),
    };
  });

  return (
    <Card>
      <SuiBox p={3}>
        {isLoading ? (
          <SuiBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="dark" size={30} />
          </SuiBox>
        ) : !isEmpty(rows) ? (
          <Table columns={ADMIN_USERS_COLUMNS} rows={rows} />
        ) : (
          <SuiBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
              group
            </Icon>
            <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
              {USER_MANAGEMENT_MESSAGES.empty}
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

export default AdminUsersTable;
